import Autosuggest, {
  AutosuggesRef,
  AutosuggestProps
} from 'core/components/inputs/autosuggest/Autosuggest'
import cn from 'utils/cn'
import { Tag, TransactionDetailed, TransactionShort } from 'types/entity'
import { useQuery } from '@tanstack/react-query'
import APIService from 'api/APIService'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import { Controller, useFormContext } from 'react-hook-form'
import Field from 'core/components/form/Field'
import Icon from 'core/components/icons/Icon'
import Button from 'core/components/buttons/Button'
import Tooltip from 'core/components/dialogs/Tooltip'
import TagChip from 'core/components/misc/Tag'

interface Props {
  readOnly?: AutosuggestProps<Tag>['readOnly']
  tagsFromServer: TransactionShort['tags']
}

const DefaultEmptySlot = (
  <div className="card bg-white flex flex-col justify-center items-center gap-2">
    <p className="text-sm text-center">No tags found</p>
  </div>
)
const LoadingSlot = (
  <div className="card bg-white flex flex-col justify-center items-center gap-2">
    <p className="text-sm text-center">Loading...</p>
  </div>
)

const TagsInput = ({ readOnly, tagsFromServer }: Props) => {
  const tagsAutosuggestRef = useRef<AutosuggesRef>(null)
  const { control, setValue, getValues } = useFormContext()
  const [tagQuery, setTagQuery] = useState('')
  const { hasTeam } = useRolesAndPermissions()
  const { data: team } = useCurrentTeam()
  const { tags_enabled } = team ?? {}

  const { data, isFetching: isLoadingTags } = useQuery({
    queryKey: ['tags', { query: tagQuery }],
    queryFn: () =>
      APIService.tags.getTags({ q: tagQuery, page: 1, page_size: 100 }),
    refetchOnWindowFocus: false
  })

  const tagsInputConfig = useMemo((): {
    show: boolean
    disabled: boolean
  } => {
    if (!hasTeam) {
      return {
        show: true,
        disabled: false
      }
    }

    return {
      show: tags_enabled ? true : !!tagsFromServer?.length,
      disabled: tags_enabled ? false : !!tagsFromServer?.length
    }
  }, [hasTeam, tags_enabled, tagsFromServer?.length])

  const tagsAutosuggestDisabled = readOnly || tagsInputConfig.disabled
  const canAddTags = tagQuery && !hasTeam

  const { data: tags } = data ?? {}
  const tagOptions = tags?.map((t) => t.name) ?? []

  const addTag = useCallback(
    (currentTags: TransactionDetailed['tags'], newTag: string) => {
      if (canAddTags === false) return

      setValue('tags', Array.from(new Set([...currentTags, ...[newTag]])), {
        shouldDirty: true
      })
      setTagQuery('')
      tagsAutosuggestRef.current?.setInputValue('')
    },
    [setValue, canAddTags]
  )

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        canAddTags &&
        tagsAutosuggestRef.current?.inputElement &&
        e.key === 'Enter' &&
        tagQuery
      ) {
        addTag(getValues('tags'), tagQuery)
      }
    }
    const inputElement = tagsAutosuggestRef.current?.inputElement
    inputElement?.addEventListener('keydown', onKeyDown)
    return () => inputElement?.removeEventListener('keydown', onKeyDown)
  }, [canAddTags, tagQuery, getValues, addTag])

  if (tagsInputConfig.show === false) return null

  return (
    <Controller
      name="tags"
      control={control}
      render={({ field }) => {
        return (
          <Tooltip
            dark
            position="left-middle"
            triggerMode="click"
            dismissOnMouseLeave
            disabled={!tagsInputConfig.disabled}
            wrapperClassName="block"
            content={
              <div className="w-56">
                Tags are no longer enabled on your team
              </div>
            }
          >
            <Field
              orientation="horizontal"
              label={<Icon name="tag-outline" size={25} color="text-dark-4" />}
            >
              <Autosuggest
                ref={tagsAutosuggestRef}
                name="tags"
                value={field.value}
                onChange={field.onChange}
                disabled={tagsAutosuggestDisabled}
                readOnly={tagsAutosuggestDisabled}
                emptySlot={
                  isLoadingTags ? (
                    LoadingSlot
                  ) : canAddTags ? (
                    <div className="card bg-white flex flex-col justify-center items-center gap-2">
                      <Button
                        icon="plus"
                        onClick={() => {
                          addTag(field.value, tagQuery)
                          tagsAutosuggestRef.current?.inputElement
                            ?.querySelector('input')
                            ?.focus()
                        }}
                      >
                        Add tag <TagChip value={tagQuery}>{tagQuery}</TagChip>
                      </Button>
                      <p className="text-sm text-center">Search results</p>
                    </div>
                  ) : (
                    DefaultEmptySlot
                  )
                }
                className={cn([
                  'w-full !outline-0 pl-0',
                  !tagsAutosuggestDisabled && 'hover:!outline-1 cursor-pointer'
                ])}
                placeholder={
                  tagsAutosuggestDisabled && !field?.value?.length
                    ? 'No tags added'
                    : tagsAutosuggestDisabled && field?.value?.length
                      ? ''
                      : 'Add tags'
                }
                variant="invisible"
                onInputChange={(value) => setTagQuery(value as string)}
                useTagOptions
                options={tagOptions}
                status={isLoadingTags ? 'loading' : 'idle'}
              />
            </Field>
          </Tooltip>
        )
      }}
    />
  )
}

export default TagsInput
